// Tiny hash router with query-inside-hash support:
//   #/game/12?tab=coach&overlay=territory
// Path segments and query params are both parsed from location.hash so the
// screenshot harness can deep-link into any UI state.

import { useEffect, useState } from 'react';

export interface Route {
  /** Path segments, e.g. ['game', '12']. Empty array = setup screen. */
  path: string[];
  query: URLSearchParams;
  raw: string;
}

export function parseHash(): Route {
  const hash = window.location.hash.replace(/^#/, '') || '/';
  const qIdx = hash.indexOf('?');
  const pathStr = qIdx >= 0 ? hash.slice(0, qIdx) : hash;
  const query = new URLSearchParams(qIdx >= 0 ? hash.slice(qIdx + 1) : '');
  return { path: pathStr.split('/').filter(Boolean), query, raw: hash };
}

/** Navigate to a new route (pushes a history entry). */
export function navigate(to: string): void {
  window.location.hash = to.startsWith('#') ? to.slice(1) : to;
}

/**
 * Patch query params on the current hash route without adding history
 * entries (null deletes a param). Fires a synthetic hashchange so
 * useHashRoute re-renders.
 */
export function setQuery(patch: Record<string, string | null>): void {
  const { path, query } = parseHash();
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) query.delete(key);
    else query.set(key, value);
  }
  const qs = query.toString();
  const next = `#/${path.join('/')}${qs ? `?${qs}` : ''}`;
  history.replaceState(null, '', next);
  window.dispatchEvent(new HashChangeEvent('hashchange'));
}

export function useHashRoute(): Route {
  const [route, setRoute] = useState<Route>(parseHash);
  useEffect(() => {
    const onChange = () => setRoute(parseHash());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}
